import clsx from 'clsx'
import { CalendarDays, ChevronLeft, ChevronRight, Download, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { DorKpis } from './DorKpis'
import { KmsSheet, OpsSheet } from './DorSheets'
import { dayHead, istToday, shiftDay } from './dorFormat'
import type { DorReportPayload } from './dorTypes'

export { dorSections, fmtCell, fmtDur, pick } from './dorFormat'

/**
 * Daily Operation Report -- the ops team's "Project UltraTech_Operation Report" workbook,
 * rebuilt from telemetry + TIMS gate scans by serving/dor_report.py: the day's headline KPIs,
 * then the workbook's two sheets (Ops Data, Kms). Fetched once per chosen date -- never
 * polled (CLAUDE.md, live data architecture). The Excel download is the same workbook.
 */

type Sheet = 'ops' | 'kms'
type Range = '7d' | 'month' | 'all'

const segWrap = 'inline-flex rounded-lg bg-slate-100 p-0.5'
const segBtn = (on: boolean) => clsx(
  'rounded-md px-3 py-1 text-[12px] font-medium transition-all',
  on ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800',
)

export function DorReport({ initialDate, initialSheet = 'ops' }: { initialDate?: string; initialSheet?: Sheet } = {}): JSX.Element {
  const today = istToday()
  const [date, setDate] = useState(initialDate ?? shiftDay(today, -1))
  const [sheet, setSheet] = useState<Sheet>(initialSheet)
  const [range, setRange] = useState<Range>('7d')
  const [reload, setReload] = useState(0)
  const [state, setState] = useState<{ status: 'loading' | 'ok' | 'error'; data?: DorReportPayload; error?: string }>({ status: 'loading' })

  useEffect(() => {
    const ctl = new AbortController()
    setState((s) => ({ status: 'loading', data: s.data }))
    fetch(`/api/dor-report?date=${encodeURIComponent(date)}`, { signal: ctl.signal, credentials: 'same-origin' })
      .then(async (r) => {
        const j = await r.json().catch(() => ({})) as DorReportPayload & { error?: string; detail?: string }
        if (!r.ok) throw new Error(j.error ?? j.detail ?? `HTTP ${r.status}`)
        setState({ status: 'ok', data: j })
      })
      .catch((e: unknown) => {
        if (!ctl.signal.aborted) setState({ status: 'error', error: e instanceof Error ? e.message : String(e) })
      })
    return () => ctl.abort()
  }, [date, reload])

  const data = state.data
  const days = useMemo(() => {
    if (!data) return []
    const all = data.days.map((d) => d.date)
    if (range === 'all') return all
    if (range === 'month') return all.filter((d) => d >= data.months.cur.first)
    return all.slice(-7)
  }, [data, range])
  const loading = state.status === 'loading'

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-[1600px] flex-col gap-4 p-3 sm:p-5">
      {/* header */}
      <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 px-4 py-4 sm:gap-4 sm:px-5">
          <div className="mr-auto min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">Daily Operation Report</h2>
              <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-white">Ultratech</span>
            </div>
            <p className="mt-0.5 text-[12.5px] text-slate-500">
              Dhar ⇄ Dhule · <span className="font-medium text-slate-700">{dayHead(date).long}</span>
              {date === today ? <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-px text-[10.5px] font-semibold text-amber-800">so far today</span> : null}
            </p>
          </div>

          <div className="flex items-center rounded-lg border border-slate-200 bg-white shadow-sm">
            <button type="button" aria-label="Previous day" onClick={() => setDate((d) => shiftDay(d, -1))}
              className="flex h-9 w-9 items-center justify-center rounded-l-lg text-slate-500 hover:bg-slate-50 hover:text-slate-900">
              <ChevronLeft size={16} />
            </button>
            <label className="flex h-9 items-center gap-2 border-x border-slate-200 px-3 text-[12.5px] font-medium text-slate-800">
              <CalendarDays size={14} className="text-slate-400" />
              <input
                type="date"
                aria-label="Report date"
                className="bg-transparent outline-none [color-scheme:light]"
                value={date}
                max={today}
                onChange={(e) => { if (e.target.value) setDate(e.target.value) }}
              />
            </label>
            <button type="button" aria-label="Next day" disabled={date >= today} onClick={() => setDate((d) => shiftDay(d, 1))}
              className="flex h-9 w-9 items-center justify-center rounded-r-lg text-slate-500 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-30">
              <ChevronRight size={16} />
            </button>
          </div>
          <button type="button" aria-label="Reload" onClick={() => setReload((n) => n + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 shadow-sm hover:text-slate-900">
            <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} />
          </button>
          <a
            href={`/api/dor-report/xlsx?date=${encodeURIComponent(date)}`}
            download
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-3.5 text-[12.5px] font-semibold text-white shadow-sm hover:bg-emerald-700"
          >
            <Download size={14} /> Excel
          </a>
        </div>
      </div>

      {state.status === 'error' ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-800">Could not build the DOR: {state.error}</div>
      ) : null}

      {!data && loading ? (
        <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-4" aria-label="Loading">
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="h-[118px] animate-pulse rounded-xl border border-slate-200/80 bg-white" />)}
        </div>
      ) : null}

      {data ? (
        <div className={clsx('flex min-w-0 flex-col gap-4 transition-opacity', loading && 'opacity-60')}>
          <DorKpis data={data} />

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-5 border-b border-slate-200" role="tablist" aria-label="DOR sheet">
              {([['ops', 'Ops Data'], ['kms', 'Kms by vehicle']] as const).map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={sheet === id} onClick={() => setSheet(id)}
                  className={clsx('-mb-px border-b-2 px-0.5 pb-2 text-[13px] font-semibold transition-colors',
                    sheet === id ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-700')}>
                  {label}
                </button>
              ))}
            </div>
            <div className={clsx(segWrap, 'ml-auto')} role="group" aria-label="Day columns">
              <button type="button" className={segBtn(range === '7d')} aria-pressed={range === '7d'} onClick={() => setRange('7d')}>Last 7 days</button>
              <button type="button" className={segBtn(range === 'month')} aria-pressed={range === 'month'} onClick={() => setRange('month')}>{data.months.cur.label}</button>
              <button type="button" className={segBtn(range === 'all')} aria-pressed={range === 'all'} onClick={() => setRange('all')}>+ {data.months.prev.label}</button>
            </div>
          </div>

          {sheet === 'ops' ? <OpsSheet data={data} days={days} /> : <KmsSheet data={data} days={days} />}
        </div>
      ) : null}
    </div>
  )
}
